import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../../data";
import { analytics } from "../../analytics";
import { LandingFooter } from "../components/LandingFooter";
import { SeoHead } from "../components/SeoHead";
import "./LoginPage.css";

const TG_CHAT = "https://t.me/+Yh772q3r-jI5Yzg6";

const IgGlyph = () => (
  <svg className="ig-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/>
  </svg>
);

interface LoginPageProps {
  isDark?: boolean;
  onToggleTheme?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ isDark, onToggleTheme }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [betaVisible, setBetaVisible] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const { t } = useTranslation();

  // Прогрев serverless-бэкенда: cold start контейнера занимает секунды,
  // и без прогрева их платит редирект «Войти» → /api/auth/instagram/start.
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/ping`).catch(() => {});
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          (e.target as HTMLElement).classList.add("in");
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleLogin = async () => {
    analytics.loginClick();
    try {
      setIsLoading(true);
      setError(null);
      const state = Math.random().toString(36).substring(7);
      localStorage.setItem("oauth_state", state);
      localStorage.setItem("auth_method", "instagram");
      window.location.href = `${API_BASE_URL}/api/auth/instagram/start?state=${state}`;
    } catch (err) {
      console.error("Failed to start OAuth:", err);
      analytics.loginError();
      setError(t("login.errorOAuthFailed"));
      setIsLoading(false);
    }
  };

  const faqItems = t("login.faq", { returnObjects: true }) as Array<{ q: string; a: string }>;
  const probCards = t("login.landingProbCards", { returnObjects: true }) as Array<{ n: string; h: string; p: string }>;
  const solCards = t("login.landingSolCards", { returnObjects: true }) as Array<{ h: string; p: string; was: string }>;
  const howSteps = t("login.landingHowSteps", { returnObjects: true }) as Array<{ n: string; h: string; p: string }>;
  const getCards = t("login.landingGetCards", { returnObjects: true }) as Array<{ h: string; p: string }>;
  const blogItems = t("login.landingBlogItems", { returnObjects: true }) as Array<{ b: string; t: string }>;
  const advItems = t("login.landingAdvItems", { returnObjects: true }) as Array<{ b: string; t: string }>;
  const accessYesItems = t("login.landingAccessYesItems", { returnObjects: true }) as string[];
  const accessNoItems = t("login.landingAccessNoItems", { returnObjects: true }) as string[];

  const probIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><path d="M12 11v6M9 14h6"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></svg>,
  ];

  const solIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z"/><path d="M9 12l2 2 4-4"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19V5M4 19h16M8 16l3-4 3 2 4-6"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><path d="M16 6l-4-4-4 4M12 2v13"/></svg>,
  ];

  const getIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.8 5.6a5 5 0 0 0-7.1 0l-1.7 1.7-1.7-1.7a5 5 0 1 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 5.5a3 3 0 0 1 0 5.6M17.5 20a6 6 0 0 0-3-5"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="4"/><path d="M10 8.5v7l5.5-3.5z" fill="currentColor" stroke="none"/></svg>,
  ];

  const advIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 5.5a3 3 0 0 1 0 5.6M17.5 20a6 6 0 0 0-3-5"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19V5M4 19h16M8 16l3-4 3 2 4-6"/></svg>,
  ];

  return (
    <div className="lp">
      <SeoHead
        title="Медиакит для Instagram — автоматический отчёт за 30 секунд"
        description="Создайте верифицированный медиакит для Instagram за 30 секунд. Данные из официального API Meta — охваты, вовлечённость, демография. Для блогеров и рекламодателей."
        canonical="/"
        faqItems={faqItems}
      />

      {/* ── BETA BAR ── */}
      {betaVisible && (
        <div className="lp-beta-bar">
          <span>{t("login.betaBannerText")}</span>
          <a href={TG_CHAT} className="lp-beta-tg" target="_blank" rel="noopener noreferrer">
            {t("login.betaBannerLink")}
          </a>
          <button className="lp-beta-close" onClick={() => setBetaVisible(false)} aria-label="Close">✕</button>
        </div>
      )}

      {/* ── NAV ── */}
      <header className="lp-nav" id="lp-top">
        <div className="wrap lp-nav-inner">
          <a className="lp-brand" href="#lp-top" aria-label="Trend">
            <span className="lp-brand-wm">TREND</span>
          </a>
          <div className="lp-nav-actions">
            {onToggleTheme && (
              <button className="lp-theme-toggle" onClick={onToggleTheme} aria-label="Toggle theme">
                {isDark
                  ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
                  : <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
                }
              </button>
            )}
            <button
              className={`lp-nav-cta btn-ig${isLoading ? " loading" : ""}`}
              onClick={handleLogin}
              disabled={isLoading}
            >
              <IgGlyph />
              <span>{isLoading ? t("login.connectingLabel") : t("login.landingNavCta")}</span>
            </button>
          </div>
        </div>
      </header>

      <main>

        {/* ── HERO ── */}
        <section className="lp-hero">
          <div className="lp-hero-glow" />
          <div className="wrap lp-hero-grid">
            <div className="lp-hero-copy">
              <h1 className="reveal d1">
                {t("login.landingHeroTitle").split(" — ")[0]} —{" "}
                <span className="serif-i">{t("login.landingHeroTitle").split(" — ")[1]?.split(" ").slice(0, 2).join(" ")}</span>{" "}
                {t("login.landingHeroTitle").split(" — ")[1]?.split(" ").slice(2).join(" ")}
              </h1>
              <p className="lead reveal d2">{t("login.landingHeroLead")}</p>
              <div className="lp-hero-actions reveal d2">
                <button
                  className={`btn-ig btn-lg${isLoading ? " loading" : ""}`}
                  onClick={handleLogin}
                  disabled={isLoading}
                >
                  <IgGlyph />
                  {isLoading ? t("login.connectingLabel") : t("login.landingHeroCta")}
                </button>
                <a href="#lp-how" className="btn-ghost btn-lg">{t("login.landingHeroGhost")}</a>
              </div>
              {error && <div className="lp-error">{error}</div>}
              <div className="lp-hero-trust reveal d3">
                <span className="lp-trust-line">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z"/><path d="M9 12l2 2 4-4"/></svg>
                  {t("login.landingHeroTrust1")}
                </span>
                <span className="lp-trust-line">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M8 12l2.5 2.5L16 9"/></svg>
                  {t("login.landingHeroTrust2")}
                </span>
              </div>
              <div className="lp-hero-avatars reveal d3">
                <span className="lp-ha-text" dangerouslySetInnerHTML={{ __html: t("login.landingHeroCount") }} />
              </div>
            </div>

            {/* MEDIAKIT PREVIEW CARD */}
            <div className="lp-kit-float reveal d2">
              <div className="lp-kit">
                <div className="lp-kit-top">
                  <div className="lp-kit-av">Н</div>
                  <div className="lp-kit-id">
                    <div className="lp-kit-name">
                      @nika.style{" "}
                      <svg className="lp-verif" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l2.4 1.8 3 .1 1 2.8 2.4 1.7-1 2.8 1 2.8-2.4 1.7-1 2.8-3 .1L12 22l-2.4-1.8-3-.1-1-2.8L3.2 15l1-2.8-1-2.8 2.4-1.7 1-2.8 3-.1z"/>
                        <path d="M8.5 12l2.2 2.2 4.8-4.8" fill="none" stroke="var(--surface)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="lp-kit-niche">Лайфстайл и бьюти · 124K подписчиков</div>
                  </div>
                  <span className="lp-kit-src">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/></svg>
                    LIVE
                  </span>
                </div>

                <div className="lp-kit-stats">
                  {[
                    { v: "8.4", s: "%", l: "Вовлечённость", up: "↑ выше нормы" },
                    { v: "1.2", s: "M", l: "Просмотры Reels", up: "↑ 18% за месяц" },
                    { v: "62", s: "%", l: "Девушки 18–24", up: "Москва · Питер" },
                  ].map(({ v, s, l, up }) => (
                    <div className="lp-kit-stat" key={l}>
                      <div className="v">{v}<small>{s}</small></div>
                      <div className="l">{l}</div>
                      <div className="up">{up}</div>
                    </div>
                  ))}
                </div>

                <div className="lp-kit-chart">
                  <div className="lp-kit-chart-head">
                    <span className="t">Охваты по неделям</span>
                    <span className="s">за 30 дней</span>
                  </div>
                  <div className="lp-bars">
                    <i style={{ height: "38%" }} /><i style={{ height: "54%" }} /><i style={{ height: "46%" }} />
                    <i style={{ height: "68%" }} /><i style={{ height: "60%" }} /><i style={{ height: "82%" }} />
                    <i className="peak" style={{ height: "100%" }} />
                  </div>
                </div>

                <div className="lp-kit-reels">
                  <div className="lp-kit-reels-head">
                    <span className="t">Лучшие Reels</span>
                    <span className="all">смотреть все →</span>
                  </div>
                  {[
                    { title: "Утренние ритуалы", date: "12 мая", views: "412K" },
                    { title: "Сборы за 5 минут", date: "3 мая", views: "389K" },
                    { title: "Обзор косметички", date: "28 апр", views: "276K" },
                  ].map(({ title, date, views }) => (
                    <div className="lp-reel-row" key={title}>
                      <div className="lp-reel-thumb" />
                      <div className="lp-reel-meta"><div className="rt">{title}</div><div className="rd">{date}</div></div>
                      <div className="lp-reel-views">{views}<span>просмотров</span></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lp-kit-badge">
                <span className="ico">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                </span>
                <div>
                  <div className="bt">{t("login.landingBlogRefreshTitle")}</div>
                  <div className="bs">{t("login.landingBadgeSub")}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PROBLEM ── */}
        <section className="lp-section" id="lp-problem">
          <div className="wrap">
            <span className="lp-eyebrow reveal">{t("login.landingProbEyebrow")}</span>
            <p className="lp-prob-quote reveal d1"
              dangerouslySetInnerHTML={{ __html: t("login.landingProbQuote").replace("<em>", '<span class="lp-hl">').replace("</em>", "</span>") }}
            />
            <div className="lp-prob-grid">
              {probCards.map(({ n, h, p }, i) => (
                <div className={`lp-prob-card reveal d${i + 1}`} key={n}>
                  <span className="lp-prob-num">{n}</span>
                  <div className="lp-prob-emoji">{probIcons[i]}</div>
                  <h3>{h}</h3>
                  <p>{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOLUTION ── */}
        <section className="lp-section lp-sol" id="lp-solution">
          <div className="wrap">
            <div className="lp-section-head reveal">
              <span className="lp-eyebrow">{t("login.landingSolEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingSolTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead">{t("login.landingSolLead")}</p>
            </div>
            <div className="lp-sol-grid">
              {solCards.map(({ h, p, was }, i) => (
                <div className={`lp-sol-card reveal d${i + 1}`} key={h}>
                  <div className="ico">{solIcons[i]}</div>
                  <h3>{h}</h3>
                  <p>{p}</p>
                  <span className="was">{was}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="lp-section" id="lp-how">
          <div className="wrap">
            <div className="lp-section-head center reveal">
              <span className="lp-eyebrow">{t("login.landingHowEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingHowTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
            </div>
            <div className="lp-steps">
              {howSteps.map(({ n, h, p }, i) => (
                <div className={`lp-step reveal d${i + 1}`} key={n}>
                  <div className="lp-step-n">{n}</div>
                  <h3>{h}</h3>
                  <p>{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── AUTH STEPS ── */}
        <section className="lp-section lp-auth-bg" id="lp-auth">
          <div className="wrap">
            <div className="lp-section-head reveal">
              <span className="lp-eyebrow">{t("login.landingAuthEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingAuthTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead">{t("login.landingAuthLead")}</p>
            </div>

            <div className="lp-auth-grid">
              <div className="lp-auth-step reveal d1">
                <div className="lp-phone-col">
                  <div className="lp-phone">
                    <div className="lp-phone-notch" />
                    <div className="lp-phone-screen">
                      <div className="lp-ps-bar">
                        <span>9:41</span>
                        <span className="lp-dots"><i/><i/><i/></span>
                      </div>
                      <div className="lp-s-login">
                        <div className="lp-s-login-top">
                          <div className="lp-ig-logo">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/></svg>
                          </div>
                          <div className="lp-ttl">Войти в Instagram</div>
                          <div className="lp-fake-field">Имя пользователя</div>
                          <div className="lp-fake-field">Пароль</div>
                          <div className="lp-fake-btn">Войти</div>
                          <div className="lp-fake-link">Забыли пароль?</div>
                        </div>
                        <div className="lp-s-login-bottom">
                          <div className="lp-fake-divider"><span>или</span></div>
                          <div className="lp-fake-signup">Создать новый аккаунт</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lp-auth-info">
                  <div className="lp-sn">{t("login.landingAuthStep1N")}</div>
                  <h3>{t("login.landingAuthStep1H")}</h3>
                  <p>{t("login.landingAuthStep1P")}</p>
                </div>
              </div>

              <div className="lp-auth-step lp-flip reveal d2">
                <div className="lp-phone-col">
                  <div className="lp-phone">
                    <div className="lp-phone-notch" />
                    <div className="lp-phone-screen">
                      <div className="lp-ps-bar">
                        <span>9:41</span>
                        <span className="lp-dots"><i/><i/><i/></span>
                      </div>
                      <div className="lp-s-perm">
                        <div className="lp-ig-oauth-header">
                          <div className="lp-ig-oauth-apps">
                            <div className="lp-ig-app-icon">
                              <svg viewBox="0 0 32 32" width="40" height="40">
                                <defs><radialGradient id="igG" cx="30%" cy="107%" r="130%"><stop offset="0%" stopColor="#ffd600"/><stop offset="30%" stopColor="#ff6930"/><stop offset="60%" stopColor="#e0367a"/><stop offset="100%" stopColor="#4a5fc1"/></radialGradient></defs>
                                <rect width="32" height="32" rx="8" fill="url(#igG)"/>
                                <rect x="7" y="7" width="18" height="18" rx="5" fill="none" stroke="white" strokeWidth="2"/>
                                <circle cx="16" cy="16" r="4.5" fill="none" stroke="white" strokeWidth="2"/>
                                <circle cx="22" cy="10" r="1.2" fill="white"/>
                              </svg>
                            </div>
                            <div className="lp-ig-oauth-arrow">
                              <svg viewBox="0 0 20 10" width="20" height="10" fill="none"><path d="M0 5h16M12 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              <svg viewBox="0 0 20 10" width="20" height="10" fill="none" style={{ transform: "scaleX(-1)" }}><path d="M0 5h16M12 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <div className="lp-ig-app-icon lp-trend-icon">T</div>
                          </div>
                          <div className="lp-ig-oauth-title">Trend хочет получить доступ к вашему аккаунту Instagram</div>
                          <div className="lp-ig-oauth-sub">@your_username</div>
                        </div>
                        <div className="lp-ig-perm-list">
                          {[
                            { name: "Статистика аккаунта", desc: "Охваты, показы, вовлечённость" },
                            { name: "Демография аудитории", desc: "Пол, возраст, география" },
                            { name: "Медиафайлы", desc: "Посты, Reels, Stories" },
                          ].map(({ name, desc }) => (
                            <div className="lp-ig-perm-item" key={name}>
                              <div className="lp-ig-perm-text">
                                <div className="lp-ig-perm-name">{name}</div>
                                <div className="lp-ig-perm-desc">{desc}</div>
                              </div>
                              <div className="lp-ig-toggle on"><div className="lp-ig-toggle-thumb"/></div>
                            </div>
                          ))}
                        </div>
                        <div className="lp-ig-oauth-btns">
                          <button className="lp-ig-btn-allow">Разрешить</button>
                          <button className="lp-ig-btn-deny">Не сейчас</button>
                        </div>
                        <div className="lp-ig-oauth-footer">Разрешая доступ, вы соглашаетесь с условиями Meta</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lp-auth-info">
                  <div className="lp-sn">{t("login.landingAuthStep2N")}</div>
                  <h3>{t("login.landingAuthStep2H")}</h3>
                  <p>{t("login.landingAuthStep2P")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── ACCESS / SECURITY ── */}
        <section className="lp-section lp-access-bg" id="lp-access">
          <div className="wrap">
            <div className="lp-section-head center reveal">
              <span className="lp-eyebrow">{t("login.landingAccessEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingAccessTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead" style={{ marginTop: 16 }}>{t("login.landingAccessLead")}</p>
            </div>
            <div className="lp-access-grid">
              <div className="lp-access-card lp-access-yes reveal d1">
                <div className="lp-access-header">
                  <div className="lp-ach-icon lp-ach-yes">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  </div>
                  <div>
                    <div className="lp-ach-title">{t("login.landingAccessYesTitle")}</div>
                    <div className="lp-ach-sub">{t("login.landingAccessYesSub")}</div>
                  </div>
                </div>
                <ul className="lp-access-list">
                  {accessYesItems.map(item => (
                    <li key={item}>
                      <span className="lp-ali lp-ali-yes"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l4 4L19 7"/></svg></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lp-access-card lp-access-no reveal d2">
                <div className="lp-access-header">
                  <div className="lp-ach-icon lp-ach-no">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
                  </div>
                  <div>
                    <div className="lp-ach-title lp-no-title">{t("login.landingAccessNoTitle")}</div>
                    <div className="lp-ach-sub">{t("login.landingAccessNoSub")}</div>
                  </div>
                </div>
                <ul className="lp-access-list">
                  {accessNoItems.map(item => (
                    <li key={item}>
                      <span className="lp-ali lp-ali-no"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 6l12 12M18 6L6 18"/></svg></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="lp-access-meta reveal d3">
              <div className="lp-mb-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z"/><path d="M9 12l2 2 4-4"/></svg>
              </div>
              <div>
                <div className="lp-mb-text">{t("login.landingAccessMeta")}</div>
                <div className="lp-mb-sub">{t("login.landingAccessMetaSub")}</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHAT YOU GET ── */}
        <section className="lp-section" id="lp-get">
          <div className="wrap">
            <div className="lp-section-head reveal">
              <span className="lp-eyebrow">{t("login.landingGetEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingGetTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead">{t("login.landingGetLead")}</p>
            </div>
            <div className="lp-get-grid">
              {getCards.map(({ h, p }, i) => (
                <div className={`lp-get-card reveal d${(i % 3) + 1}`} key={h}>
                  <div className="ico">{getIcons[i]}</div>
                  <h3>{h}</h3>
                  <p>{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FOR BLOGGERS ── */}
        <section className="lp-section lp-sol" id="lp-bloggers">
          <div className="wrap lp-blog-grid">
            <div className="reveal">
              <span className="lp-eyebrow">{t("login.landingBlogEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingBlogTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead" style={{ marginTop: 16 }}>{t("login.landingBlogLead")}</p>
              <ul className="lp-bene-list">
                {blogItems.map(({ b, t: txt }) => (
                  <li key={b}>
                    <span className="ck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l4 4L19 7"/></svg></span>
                    <span><b>{b}</b> {txt}</span>
                  </li>
                ))}
              </ul>
              <button
                className={`btn-ig btn-lg${isLoading ? " loading" : ""}`}
                onClick={handleLogin}
                disabled={isLoading}
                style={{ marginTop: 30 }}
              >
                <IgGlyph />
                {isLoading ? t("login.connectingLabel") : t("login.landingBlogCta")}
              </button>
            </div>
            <div className="lp-blog-visual reveal d2">
              <div className="lp-blog-mini">
                <div className="lp-kit-reels-head" style={{ marginBottom: 14 }}>
                  <span className="t" style={{ fontSize: "0.92rem" }}>{t("login.landingBlogLinkTitle")}</span>
                  <span className="all">{t("login.landingBlogLinkCopy")}</span>
                </div>
                <div className="lp-cat-search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16, color: "var(--gold-deep)" }}><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>
                  <span className="mono" style={{ fontSize: "0.82rem" }}>trend.nasonov.fun/<b style={{ color: "var(--gold-deep)" }}>nika.style</b></span>
                </div>
                <div className="lp-kit-stats" style={{ marginTop: 16 }}>
                  <div className="lp-kit-stat"><div className="v">124<small>K</small></div><div className="l">Подписчики</div></div>
                  <div className="lp-kit-stat"><div className="v">8.4<small>%</small></div><div className="l">Вовлечённость</div></div>
                  <div className="lp-kit-stat"><div className="v">340<small>K</small></div><div className="l">Охват поста</div></div>
                </div>
              </div>
              <div className="lp-blog-mini" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span className="lp-refresh-ico">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 23, height: 23 }}><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                </span>
                <div>
                  <b style={{ display: "block" }}>{t("login.landingBlogRefreshTitle")}</b>
                  <span className="lp-muted">{t("login.landingBlogRefreshText")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FOR ADVERTISERS (dark band) ── */}
        <section className="lp-section lp-adv-section" id="lp-advertisers" style={{ paddingInline: 0 }}>
          <div className="lp-adv reveal">
            <div className="lp-adv-glow" />
            <div className="lp-adv-inner">
              <div>
                <span className="lp-eyebrow lp-eyebrow-gold">{t("login.landingAdvEyebrow")}</span>
                <h2 className="lp-h2 display" style={{ marginTop: 16, color: "var(--band-ink)" }}
                  dangerouslySetInnerHTML={{ __html: t("login.landingAdvTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
                />
                <p className="lead" style={{ marginTop: 16, color: "var(--band-soft)" }}>{t("login.landingAdvLead")}</p>
                <ul className="lp-adv-find">
                  {advItems.map(({ b, t: txt }, i) => (
                    <li key={b}>
                      <span className="ico">{advIcons[i]}</span>
                      <div><b>{b}</b><br /><span>{txt}</span></div>
                    </li>
                  ))}
                </ul>
                <Link to="/catalog" className="btn-light btn-lg lp-adv-btn" style={{ marginTop: 30 }}>
                  {t("login.landingAdvCta")}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="lp-section" id="lp-faq">
          <div className="wrap">
            <div className="lp-section-head center reveal">
              <span className="lp-eyebrow">{t("login.landingFaqEyebrow")}</span>
              <h2 className="lp-h2 display" style={{ marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: t("login.landingFaqTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
            </div>
            <div className="lp-faq-wrap">
              {(Array.isArray(faqItems) && faqItems.length > 0 ? faqItems : []).map(({ q, a }, i) => (
                <div className={`lp-faq-item${openFaq === i ? " open" : ""}`} key={i}>
                  <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    {q}
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14"/></svg>
                    </span>
                  </button>
                  <div className="lp-faq-a" style={{ maxHeight: openFaq === i ? 300 : 0 }}>
                    <p>{a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="lp-section lp-cta" id="lp-cta">
          <div className="wrap">
            <div className="lp-cta-box reveal">
              <div className="lp-glow" />
              <h2 className="display"
                dangerouslySetInnerHTML={{ __html: t("login.landingCtaTitle").replace("<em>", '<span class="serif-i">').replace("</em>", "</span>") }}
              />
              <p className="lead">{t("login.landingCtaLead")}</p>
              <div className="lp-hero-actions" style={{ justifyContent: "center", marginTop: 32 }}>
                <button
                  className={`btn-ig btn-lg${isLoading ? " loading" : ""}`}
                  onClick={handleLogin}
                  disabled={isLoading}
                >
                  <IgGlyph />
                  {isLoading ? t("login.connectingLabel") : t("login.landingHeroCta")}
                </button>
              </div>
              {error && <div className="lp-error" style={{ marginTop: 16 }}>{error}</div>}
            </div>
          </div>
        </section>

      </main>

      <LandingFooter />

    </div>
  );
};
